import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import humps from "humps";
import { debounce } from "lodash";
import * as qs from "query-string";
import React from "react";
import { FormattedMessage } from "react-intl";

import ProductsAdminResource from "nvent-web-admin/resources/ProductsResource";
import ProjectsAdminResource from "nvent-web-admin/resources/ProjectResource";
import UsersAdminResource from "nvent-web-admin/resources/UsersResource";
import { NotificationsStore } from "nvent-web/stores/Notifications";
import { buildTransformers } from "nvent-web/utils/transformers";

import AccessoryResource from "./resources/AccessoryResource";
import { AuthResource } from "./resources/AuthResource";
import OptionsResource from "./resources/OptionsResource";
import PhotoResource from "./resources/PhotoResource";
import ProductCatalogResource from "./resources/ProductCatalogResource";
import ProductResource from "./resources/ProductResource";
import { ProfileResource } from "./resources/ProfileResource";
import ProjectsResource from "./resources/ProjectResource";
import RoomsResource from "./resources/RoomResource";
import { TeamInstallersResource } from "./resources/TeamInstallersResource";
import { TeamProjectsResource } from "./resources/TeamProjectsResource";
import ThermostatResource from "./resources/ThermostatResource";

export default class Api {
  accessToken = "";

  readonly admin: {
    users: UsersAdminResource;
    projects: ProjectsAdminResource;
    products: ProductsAdminResource;
  };

  readonly auth: AuthResource;
  readonly profile: ProfileResource;
  readonly projects: ProjectsResource;
  readonly rooms: RoomsResource;
  readonly products: ProductResource;
  readonly thermostat: ThermostatResource;
  readonly accessories: AccessoryResource;
  readonly productCatalog: ProductCatalogResource;
  readonly photos: PhotoResource;
  readonly options: OptionsResource;
  readonly team: {
    installers: TeamInstallersResource;
    projects: TeamProjectsResource;
  };

  readonly http: AxiosInstance;

  private showNetworkError = debounce(() => {
    this.notifications.createError(<FormattedMessage id="error.nerworkError" />);
  }, 250);

  constructor(baseURL: string, private notifications: NotificationsStore) {
    this.http = axios.create({
      baseURL,
      transformRequest: buildTransformers(
        (data) => (data instanceof FormData ? data : humps.decamelizeKeys(data)),
        axios.defaults.transformRequest
      ),
      transformResponse: buildTransformers(axios.defaults.transformResponse, (data) =>
        data instanceof Blob ? data : humps.camelizeKeys(data)
      ),
    });
    this.http.interceptors.request.use(this.addAccessToken.bind(this));

    this.http.interceptors.response.use(undefined, this.errorResponseHandler);

    this.auth = new AuthResource(this);
    this.profile = new ProfileResource(this);
    this.projects = new ProjectsResource(this);
    this.rooms = new RoomsResource(this);
    this.products = new ProductResource(this);
    this.thermostat = new ThermostatResource(this);
    this.productCatalog = new ProductCatalogResource(this);
    this.photos = new PhotoResource(this);
    this.options = new OptionsResource(this);
    this.accessories = new AccessoryResource(this);

    this.admin = {
      users: new UsersAdminResource(this),
      projects: new ProjectsAdminResource(this),
      products: new ProductsAdminResource(this),
    };

    this.team = {
      installers: new TeamInstallersResource(this),
      projects: new TeamProjectsResource(this),
    };
  }

  async addAccessToken(config: AxiosRequestConfig): Promise<AxiosRequestConfig> {
    if (!config.headers.Authorization && this.accessToken) {
      config.headers.Authorization = `Bearer ${this.accessToken}`;
    }

    return config;
  }

  download(config: { url: string; params?: { [key: string]: any } }) {
    const params = humps.decamelizeKeys({ ...config.params, token: this.accessToken });
    const url = `${this.http.defaults.baseURL}${config.url}?${qs.stringify(params, { arrayFormat: "bracket" })}`;
    window.location.href = url;
  }

  private errorResponseHandler = (error: AxiosError) => {
    if (error.request && !error.response) {
      this.showNetworkError();
    }
    return Promise.reject(error);
  };
}
